/**
 * Login dialog component for authentication.
 */

import { authService } from './AuthService.js';

/**
 * Modal dialog for user login.
 */
export class LoginDialog {
  private overlay: HTMLElement | null = null;
  private dialog: HTMLElement | null = null;
  private usernameInput: HTMLInputElement | null = null;
  private passwordInput: HTMLInputElement | null = null;
  private errorMessage: HTMLElement | null = null;
  private submitButton: HTMLButtonElement | null = null;

  /**
   * Open the login dialog.
   * @returns Promise that resolves to true if login was successful
   */
  async open(): Promise<boolean> {
    return new Promise((resolve) => {
      this.createDialog(resolve);
    });
  }

  private createDialog(resolve: (success: boolean) => void): void {
    // Create overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm';
    this.overlay.style.animation = 'fadeIn 150ms ease-out';

    // Create dialog
    this.dialog = document.createElement('div');
    this.dialog.className = 'bg-[#1a1a2e] border border-[var(--accent-alpha-27)] rounded-lg shadow-2xl w-full max-w-md p-6';
    this.dialog.style.animation = 'slideUp 200ms ease-out';

    // Title
    const title = document.createElement('h2');
    title.className = 'text-xl font-bold text-[var(--accent)] font-mono mb-4';
    title.textContent = 'Inici sessió';
    this.dialog.appendChild(title);

    // Form
    const form = document.createElement('form');
    form.className = 'space-y-4';

    // Username field
    const usernameGroup = document.createElement('div');
    usernameGroup.className = 'space-y-1';
    const usernameLabel = document.createElement('label');
    usernameLabel.className = 'block text-sm font-medium text-[#a0a0a0] font-mono';
    usernameLabel.textContent = 'Nom d\'usuari';
    usernameGroup.appendChild(usernameLabel);

    this.usernameInput = document.createElement('input');
    this.usernameInput.type = 'text';
    this.usernameInput.className = 'w-full px-3 py-2 bg-[#0d1117] border border-[var(--accent-alpha-27)] rounded-md text-[#e0e0e0] font-mono focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
    this.usernameInput.placeholder = 'usuari';
    this.usernameInput.autocomplete = 'username';
    usernameGroup.appendChild(this.usernameInput);
    form.appendChild(usernameGroup);

    // Password field
    const passwordGroup = document.createElement('div');
    passwordGroup.className = 'space-y-1';
    const passwordLabel = document.createElement('label');
    passwordLabel.className = 'block text-sm font-medium text-[#a0a0a0] font-mono';
    passwordLabel.textContent = 'Contrasenya';
    passwordGroup.appendChild(passwordLabel);

    this.passwordInput = document.createElement('input');
    this.passwordInput.type = 'password';
    this.passwordInput.className = 'w-full px-3 py-2 bg-[#0d1117] border border-[var(--accent-alpha-27)] rounded-md text-[#e0e0e0] font-mono focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
    this.passwordInput.placeholder = '••••••••';
    this.passwordInput.autocomplete = 'current-password';
    passwordGroup.appendChild(this.passwordInput);
    form.appendChild(passwordGroup);

    // Error message
    this.errorMessage = document.createElement('div');
    this.errorMessage.className = 'text-red-500 text-sm font-mono hidden';
    form.appendChild(this.errorMessage);

    // Buttons
    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'flex gap-3 pt-2';

    // Cancel button
    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'flex-1 py-2 px-4 border border-[var(--accent-alpha-27)] rounded-md text-[#a0a0a0] font-mono hover:bg-[var(--accent-alpha-13)] transition-colors';
    cancelButton.textContent = 'Cancel·la';
    cancelButton.addEventListener('click', () => {
      this.close();
      resolve(false);
    });
    buttonGroup.appendChild(cancelButton);

    // Submit button
    this.submitButton = document.createElement('button');
    this.submitButton.type = 'submit';
    this.submitButton.className = 'flex-1 py-2 px-4 bg-[var(--accent)] text-[#0d1117] rounded-md font-mono font-bold hover:brightness-110 transition-all';
    this.submitButton.textContent = 'Entra';
    buttonGroup.appendChild(this.submitButton);

    form.appendChild(buttonGroup);

    // Form submit handler
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleSubmit(resolve);
    });

    this.dialog.appendChild(form);
    this.overlay.appendChild(this.dialog);

    // Close on overlay click
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
        resolve(false);
      }
    });

    // Close on Escape
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
        document.removeEventListener('keydown', escHandler);
        resolve(false);
      }
    };
    document.addEventListener('keydown', escHandler);

    document.body.appendChild(this.overlay);

    // Focus username input
    setTimeout(() => this.usernameInput?.focus(), 100);
  }

  private async handleSubmit(resolve: (success: boolean) => void): Promise<void> {
    if (!this.usernameInput || !this.passwordInput || !this.submitButton || !this.errorMessage) {
      return;
    }

    const username = this.usernameInput.value.trim();
    const password = this.passwordInput.value;

    if (!username || !password) {
      this.showError('Omple tots els camps');
      return;
    }

    // Disable button and show loading state
    this.submitButton.disabled = true;
    this.submitButton.textContent = 'Entrant...';
    this.errorMessage.classList.add('hidden');

    const success = await authService.login(username, password);

    if (success) {
      this.close();
      resolve(true);
    } else {
      this.showError('Nom d\'usuari o contrasenya incorrectes');
      this.submitButton.disabled = false;
      this.submitButton.textContent = 'Entra';
      this.passwordInput.value = '';
      this.passwordInput.focus();
    }
  }

  private showError(message: string): void {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.classList.remove('hidden');
    }
  }

  private close(): void {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    this.overlay = null;
    this.dialog = null;
    this.usernameInput = null;
    this.passwordInput = null;
    this.errorMessage = null;
    this.submitButton = null;
  }
}
